import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Calendar, LogOut, Users, CalendarClock, Mail, RefreshCw,
  ChevronLeft, ChevronRight, ShieldCheck, Inbox,
} from 'lucide-react';
import { useAuth, jsonFetch } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const TABS = [
  { key: 'contacts', label: 'Contacts', icon: Users, path: '/api/admin/submissions/contacts' },
  { key: 'consultations', label: 'Consultations', icon: CalendarClock, path: '/api/admin/submissions/consultations' },
  { key: 'newsletter', label: 'Newsletter', icon: Mail, path: '/api/admin/submissions/newsletter' },
];

// Column config per tab — keeps the table renderer generic.
const COLUMNS = {
  contacts: [
    { key: 'timestamp', label: 'Received', width: 'w-40', type: 'date' },
    { key: 'name', label: 'Name', width: 'w-40' },
    { key: 'email', label: 'Email', width: 'w-56', type: 'email' },
    { key: 'phone', label: 'Phone', width: 'w-32' },
    { key: 'company', label: 'Company', width: 'w-40' },
    { key: 'service', label: 'Service', width: 'w-40' },
    { key: 'message', label: 'Message', width: 'min-w-[16rem]', type: 'long' },
  ],
  consultations: [
    { key: 'timestamp', label: 'Received', width: 'w-40', type: 'date' },
    { key: 'name', label: 'Name', width: 'w-40' },
    { key: 'email', label: 'Email', width: 'w-56', type: 'email' },
    { key: 'phone', label: 'Phone', width: 'w-32' },
    { key: 'service', label: 'Service', width: 'w-44' },
    { key: 'preferredDate', label: 'Preferred date', width: 'w-32' },
    { key: 'preferredTime', label: 'Preferred time', width: 'w-32' },
  ],
  newsletter: [
    { key: 'timestamp', label: 'Received', width: 'w-40', type: 'date' },
    { key: 'email', label: 'Email', width: 'w-72', type: 'email' },
    { key: 'status', label: 'Status', width: 'w-28' },
  ],
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('contacts');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const columns = COLUMNS[activeTab];
  const currentTab = TABS.find((t) => t.key === activeTab);

  // Debounce search input to avoid a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to first page whenever filters change.
  useEffect(() => { setPage(1); }, [activeTab, debouncedQ, startDate, endDate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (debouncedQ) params.set('q', debouncedQ);
      if (startDate) params.set('start', startDate);
      if (endDate) params.set('end', endDate);
      const data = await jsonFetch(`${API}${currentTab.path}?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e.status === 401) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(e.message || 'Failed to load submissions');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentTab.path, debouncedQ, startDate, endDate, page, navigate]);

  useEffect(() => { load(); }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const clearFilters = () => { setQ(''); setStartDate(''); setEndDate(''); };
  const hasFilters = Boolean(q || startDate || endDate);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-teal-700 leading-none">WPAA Admin</p>
              <p className="text-sm font-semibold text-gray-900 leading-tight">Submissions Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-gray-500" data-testid="admin-current-user">
              {admin?.email}
            </span>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="h-9"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} className="mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" role="tablist" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.key === activeTab;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-teal-600 border-teal-600 text-white shadow-sm'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-teal-300 hover:text-teal-700'
                }`}
                data-testid={`admin-tab-${t.key}`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-0">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={
                  activeTab === 'newsletter'
                    ? 'Search email…'
                    : 'Search name, email, company, message…'
                }
                className="pl-9 h-10"
                data-testid="admin-search-input"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 sm:flex-initial min-w-[9rem]">
                <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-8 pr-2 h-10 border border-gray-200 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-400"
                  aria-label="Start date"
                  data-testid="admin-start-date"
                />
              </div>
              <span className="text-xs text-gray-400">to</span>
              <div className="relative flex-1 sm:flex-initial min-w-[9rem]">
                <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full pl-8 pr-2 h-10 border border-gray-200 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-400"
                  aria-label="End date"
                  data-testid="admin-end-date"
                />
              </div>
              {hasFilters && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="h-10 text-xs text-gray-500 hover:text-gray-800"
                  data-testid="admin-clear-filters"
                >
                  Clear
                </Button>
              )}
              <Button
                variant="outline"
                onClick={load}
                className="h-10"
                data-testid="admin-refresh-btn"
                aria-label="Refresh"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/60">
            <p className="text-sm text-gray-600" data-testid="admin-results-count">
              {loading ? 'Loading…' : `${total.toLocaleString()} ${currentTab.label.toLowerCase()} ${total === 1 ? 'submission' : 'submissions'}`}
              {hasFilters && !loading && <span className="text-gray-400"> · filtered</span>}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Page {page} of {pageCount}</span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="h-8 w-8 p-0"
                data-testid="admin-page-prev"
              >
                <ChevronLeft size={14} />
              </Button>
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount || loading}
                className="h-8 w-8 p-0"
                data-testid="admin-page-next"
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>

          {error && (
            <div className="px-5 py-4 text-sm text-red-700 bg-red-50 border-b border-red-100" data-testid="admin-error">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead className="bg-white text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={`text-left font-semibold px-5 py-3 ${c.width || ''}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-5 py-16 text-center"
                      data-testid="admin-empty-state"
                    >
                      <Inbox size={28} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-sm font-medium text-gray-700">No submissions found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {hasFilters ? 'Try adjusting the filters.' : 'New submissions will appear here.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/60 align-top">
                      {columns.map((c) => {
                        const raw = row[c.key];
                        let content;
                        if (c.type === 'date') {
                          content = <span className="text-gray-600 whitespace-nowrap">{formatDate(raw)}</span>;
                        } else if (c.type === 'email' && raw) {
                          content = (
                            <a
                              href={`mailto:${raw}`}
                              className="text-teal-700 hover:underline truncate block max-w-full"
                              title={raw}
                            >
                              {raw}
                            </a>
                          );
                        } else if (c.type === 'long') {
                          content = (
                            <div className="text-gray-700 max-w-[28rem]">
                              <p className="line-clamp-2 break-words" title={raw || ''}>
                                {raw || '—'}
                              </p>
                            </div>
                          );
                        } else {
                          content = <span className="text-gray-800">{raw || '—'}</span>;
                        }
                        return (
                          <td key={c.key} className={`px-5 py-3 ${c.width || ''}`}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
